import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { UserPlus, ShieldCheck, ShieldOff, Mail, ScrollText, ArrowLeft } from 'lucide-react';
import UserEditorModal from '../../components/admin/UserEditorModal';

const STATUS_BADGE = {
  active:   { label: 'Active',   variant: 'default' },
  invited:  { label: 'Invited',  variant: 'secondary' },
  disabled: { label: 'Disabled', variant: 'outline' },
};

export default function UserManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingUser, setEditingUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (roleFilter !== 'all') params.role = roleFilter;
      const resp = await api.get('/admin/users', { params });
      setRows(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Kon gebruikers niet laden';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter, roleFilter]); // eslint-disable-line

  const activeSuperAdminCount = useMemo(
    () => rows.filter((r) => r.role === 'super_admin' && r.status === 'active').length,
    [rows]
  );

  const handleInvite = () => {
    setEditingUser(null);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    setEditingUser(row);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleSaved = () => {
    load();
  };

  const handleDisable = async (row) => {
    if (row.role === 'super_admin' && row.status === 'active' && activeSuperAdminCount <= 1) {
      toast.error('Laatste actieve super_admin kan niet worden uitgeschakeld.');
      return;
    }
    try {
      await api.post(`/admin/users/${row.id}/disable`);
      toast.success(`${row.email} uitgeschakeld`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Uitschakelen mislukt');
    }
  };

  const handleEnable = async (row) => {
    try {
      await api.post(`/admin/users/${row.id}/enable`);
      toast.success(`${row.email} ingeschakeld`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Inschakelen mislukt');
    }
  };

  const handleResend = async (row) => {
    try {
      await api.post(`/admin/users/${row.id}/resend-invite`);
      toast.success(`Uitnodiging opnieuw verstuurd naar ${row.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Opnieuw versturen mislukt');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="user-management-page">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Gebruikersbeheer</h1>
              <p className="text-xs text-gray-500">Super_admin only · ingelogd als {user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/audit-log')} data-testid="open-audit-log">
              <ScrollText className="h-4 w-4 mr-1" /> Audit log
            </Button>
            <Button onClick={handleInvite} data-testid="invite-user-button">
              <UserPlus className="h-4 w-4 mr-1" /> Gebruiker uitnodigen
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-500">Filter:</div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle statussen</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="invited">Invited</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-40" data-testid="role-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle rollen</SelectItem>
                  <SelectItem value="super_admin">Super admin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500">Laden…</p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="users-empty-state">Nog geen gebruikers. Klik op &quot;Gebruiker uitnodigen&quot; om de eerste toe te voegen.</p>
            ) : (
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>E-mail</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead className="text-right">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const sb = STATUS_BADGE[row.status] || { label: row.status || '—', variant: 'outline' };
                    const isLastSuperAdmin = row.role === 'super_admin' && row.status === 'active' && activeSuperAdminCount <= 1;
                    return (
                      <TableRow key={row.id} data-testid={`user-row-${row.id}`}>
                        <TableCell className="font-medium">{row.email || '—'}</TableCell>
                        <TableCell><Badge variant="outline">{row.role || '—'}</Badge></TableCell>
                        <TableCell><Badge variant={sb.variant}>{sb.label}</Badge></TableCell>
                        <TableCell className="text-xs text-gray-600">
                          {row.scope?.type === 'global'
                            ? 'Global'
                            : row.scope?.type === 'village'
                              ? `Village (${(row.scope.village_ids || []).length})`
                              : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleEdit(row)} data-testid={`edit-user-${row.id}`}>Bewerken</Button>
                            {row.status === 'invited' && (
                              <Button size="sm" variant="ghost" onClick={() => handleResend(row)} data-testid={`resend-invite-${row.id}`}>
                                <Mail className="h-4 w-4 mr-1" /> Opnieuw versturen
                              </Button>
                            )}
                            {row.status === 'disabled' ? (
                              <Button size="sm" variant="ghost" onClick={() => handleEnable(row)} data-testid={`enable-user-${row.id}`}>
                                <ShieldCheck className="h-4 w-4 mr-1" /> Inschakelen
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDisable(row)}
                                disabled={isLastSuperAdmin}
                                title={isLastSuperAdmin ? 'Laatste actieve super_admin kan niet worden uitgeschakeld' : ''}
                                data-testid={`disable-user-${row.id}`}
                              >
                                <ShieldOff className="h-4 w-4 mr-1" /> Uitschakelen
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      <UserEditorModal
        open={modalOpen}
        mode={modalMode}
        user={editingUser}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}
